import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAdminProducts } from "@/hooks/useProducts";
import { formatOre, swapSortOrder } from "@/lib/products";
import NewProductDialog from "@/components/admin/NewProductDialog";
import { handleApiError } from "@/lib/errors";

const AdminProducts = () => {
  const queryClient = useQueryClient();
  const { data: products, isLoading, error } = useAdminProducts();
  const [newOpen, setNewOpen] = useState(false);
  const [reordering, setReordering] = useState<string | null>(null);

  async function handleMove(index: number, direction: -1 | 1) {
    if (!products) return;
    const target = index + direction;
    if (target < 0 || target >= products.length) return;
    const a = products[index];
    const b = products[target];
    setReordering(a.id);
    try {
      await swapSortOrder(
        { id: a.id, sort_order: a.sort_order },
        { id: b.id, sort_order: b.sort_order }
      );
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    } catch (err) {
      handleApiError(err, "Kunne ikke flytte produktet.");
    } finally {
      setReordering(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Produkter</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Endre priser, beskrivelser, lager og bilder. Inaktive produkter forsvinner fra butikken.
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nytt produkt
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster …</p>
      ) : error ? (
        <p className="text-sm text-destructive">Kunne ikke laste produkter.</p>
      ) : !products || products.length === 0 ? (
        <div className="rounded-lg border bg-card p-12 text-center text-muted-foreground">
          Ingen produkter enda. Klikk "Nytt produkt" for å opprette ditt første.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="w-16 px-2 py-3 text-left text-sm font-medium text-muted-foreground">
                  Rekkefølge
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Produkt</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Kategori</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Pris</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Lager</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Bilder</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {products.map((p, i) => {
                const priceLabel =
                  p.min_price_ore == null
                    ? "—"
                    : p.min_price_ore === p.max_price_ore
                    ? formatOre(p.min_price_ore)
                    : `${formatOre(p.min_price_ore)} – ${formatOre(p.max_price_ore!)}`;
                const isReordering = reordering === p.id;
                return (
                  <tr key={p.id} className="hover:bg-muted/20">
                    <td className="px-2 py-3">
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          aria-label="Flytt opp"
                          onClick={() => void handleMove(i, -1)}
                          disabled={i === 0 || isReordering}
                          className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label="Flytt ned"
                          onClick={() => void handleMove(i, 1)}
                          disabled={i === products.length - 1 || isReordering}
                          className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/admin/products/${p.id}`} className="hover:underline">
                        {p.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{p.category}</td>
                    <td className="px-4 py-3 text-sm">{priceLabel}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {p.total_inventory ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{p.image_count}</td>
                    <td className="px-4 py-3">
                      {p.is_active ? (
                        <Badge variant="default">Aktiv</Badge>
                      ) : (
                        <Badge variant="secondary">Skjult</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/products/${p.id}`}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        Rediger
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewProductDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
};

export default AdminProducts;
